import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Jönköping?", a: "Ansökan görs till Socialförvaltningen / tillståndsenheten i Jönköpings kommun. Verksamhet i annan kommun i Jönköpings län (t.ex. Habo, Mullsjö, Vaggeryd, Nässjö) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Jönköping?", a: "Jönköping kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Jönköping?", a: "Jönköping kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-jonkoping")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Jönköping" },
      ]}
      title="Serveringstillstånd Jönköping – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Jönköping: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.jonkoping, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Jönköpings kommun – Tillståndsenheten (socialtjänsten)",
        address: "Jönköpings kommun, Kontaktcenter, 551 89 Jönköping",
        phone: "036-10 50 00 (kontaktcenter) / 036-10 27 10 (enhetschef tillståndsenheten)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd från komplett ansökan",
        feeExamples: [
          "Stadigvarande tillstånd/gårdsförsäljningstillstånd: ca 14 600 kr",
          "Tillfälligt tillstånd till allmänheten: ca 6 700 kr",
          "Kunskapsprov: ca 600 kr per provtillfälle",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.jonkoping,
      }}
      sections={[
        { h2: "Var ansöker du i Jönköping?", body: "Socialförvaltningen / tillståndsenheten i Jönköpings kommun ansvarar för serveringstillstånd i Jönköping kommun. För verksamhet i kringliggande kommuner som Habo, Mullsjö, Vaggeryd, Nässjö ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Jönköping kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Jönköping har särskilda hänsyn att ta vid serveringar nära Vättern, hamnen och Elmia-mässan." },
        { h2: "Kunskapsprovet i Jönköping", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Avgifter och handläggningstid i Jönköping", body: "Jönköpings kommun tar ut ca 14 600 kr för en ansökan om stadigvarande serverings- eller gårdsförsäljningstillstånd, medan ett tillfälligt tillstånd till allmänheten kostar ca 6 700 kr och kunskapsprovet ca 600 kr per tillfälle. Tillståndsenheten nås via tillstand@jonkoping.se eller kommunens kontaktcenter på 036-10 50 00. Räkna med 8–12 veckor från komplett ansökan till beslut om stadigvarande tillstånd." },
        { h2: "Tillsyn och uppföljning", body: "Jönköping kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Ansök om serveringstillstånd i Jönköping – guide" },
      { name: "description", content: "Praktisk guide till ansökan om serveringstillstånd i Jönköping: lokal, lämplighet och kunskapsprovet." },
      { property: "og:title", content: "Ansökan – serveringstillstånd Jönköping" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Jönköping." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-jonkoping" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-jonkoping" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Jönköping", url: "https://alkoholprovet.se/serveringstillstand-jonkoping" },
      ]),
    ],
  }),
});
